import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Chip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  TextField,
  Button,
  Snackbar,
  Stack,
} from '@mui/material';
import { useTradeStore } from '../store/tradeStore';
import { useMarketStore } from '../store/marketStore';
import { useConditionStore } from '../store/conditionStore';
import BuyConfirmDialog from '../components/BuyConfirmDialog';
import { getPriceColor, formatPct, formatNumber, formatMoney } from '../utils/format';
import { fetchCandidateDates } from '../api/trades';
import { fetchAutoTradeStatus, updateAutoTradeConfig } from '../api/autoTrade';
import { evaluateConditions } from '../api/conditions';
import { fetchLatestSelfCheck, runSelfCheckNow } from '../api/selfCheck';
import RefreshIcon from '@mui/icons-material/Refresh';
import BugReportIcon from '@mui/icons-material/BugReport';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { RealtimeQuote, AutoTradeStatus, SelfCheckLog, Candidate } from '../types';

/**
 * 首页 — 买入候选看板
 * 展示符合入选条件的候选股 + 买入按钮 + 暂停状态提示
 * 支持按日期查看历史候选股 + 配置自动买入金额
 * 候选股展示顺序与「撞顶时优先选」条件完全一致（2026-09-06）
 */
export default function HomePage() {
  const { candidates, candidateDate, candidateSource, fetchCandidates, buy, actionLoading } = useTradeStore();
  const { breadth } = useMarketStore();
  const { conditions, fetchConditions } = useConditionStore();
  const [buyDialogOpen, setBuyDialogOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<RealtimeQuote | null>(null);
  const [candidatesWithQuotes, setCandidatesWithQuotes] = useState<any[]>([]);
  const [availableDates, setAvailableDates] = useState<{ date: string; count: number }[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [autoTradeStatus, setAutoTradeStatus] = useState<AutoTradeStatus | null>(null);
  const [buyAmount, setBuyAmount] = useState<string>('10000');
  const [buyTime, setBuyTime] = useState<string>('10:00');
  const [savingAmount, setSavingAmount] = useState(false);
  const [savingTime, setSavingTime] = useState(false);
  const [screening, setScreening] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'info' });
  const [selfCheck, setSelfCheck] = useState<SelfCheckLog | null>(null);
  const [selfCheckRunning, setSelfCheckRunning] = useState(false);
  const [showSelfCheckDetail, setShowSelfCheckDetail] = useState(false);
  const autoScreenRef = useRef(false);

  // 加载自检状态（60 秒轮询，与后端自检频率一致）
  const loadSelfCheck = useCallback(async () => {
    try {
      const latest = await fetchLatestSelfCheck();
      setSelfCheck(latest);
    } catch (e) {
      console.error('获取自检状态失败', e);
    }
  }, []);

  useEffect(() => {
    loadSelfCheck();
    const timer = window.setInterval(loadSelfCheck, 60000);
    return () => window.clearInterval(timer);
  }, [loadSelfCheck]);

  // 手动立即自检
  const handleRunSelfCheck = async () => {
    setSelfCheckRunning(true);
    try {
      await runSelfCheckNow();
      await loadSelfCheck();
      setShowSelfCheckDetail(true);
      setSnackbar({ open: true, message: '自检完成', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: `自检失败: ${e.message || e}`, severity: 'error' });
    } finally {
      setSelfCheckRunning(false);
    }
  };

  // 加载候选股日期列表
  const loadDates = useCallback(async () => {
    try {
      const dates = await fetchCandidateDates();
      setAvailableDates(dates);
    } catch (e) {
      console.error('获取候选股日期列表失败', e);
    }
  }, []);

  // 加载自动交易状态（含买入金额和时间配置）
  const loadAutoTradeStatus = useCallback(async () => {
    try {
      const status = await fetchAutoTradeStatus();
      setAutoTradeStatus(status);
      setBuyAmount(String(status.buyAmountPerStock || 10000));
      setBuyTime(status.buyTime || '10:00');
    } catch (e) {
      console.error('获取自动交易状态失败', e);
    }
  }, []);

  // 自动刷新今日候选：工作日收盘后（≥15:00）且候选数据仍落后于今日时，自动触发一次筛选，
  // 完成后 candidateDate 更新为今日，autoScreenRef 标记已触发，避免重复全量筛选。
  const maybeAutoScreen = useCallback(async () => {
    if (autoScreenRef.current) return;
    const now = new Date();
    const day = now.getDay();
    const hour = now.getHours();
    // 非工作日或未到收盘，无需检查（盘前/盘中沿用上一交易日候选，与手动刷新行为一致）
    if (day < 1 || day > 5 || hour < 15) return;

    try {
      await fetchCandidates();
      const { candidateDate: latestDate } = useTradeStore.getState();
      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

      if (latestDate && latestDate < todayStr) {
        autoScreenRef.current = true; // 先标记，避免并发/重复触发
        setScreening(true);
        try {
          await evaluateConditions();
          await loadDates();
          await fetchCandidates();
          setSnackbar({ open: true, message: '已自动刷新今日候选股', severity: 'info' });
        } catch (e: any) {
          console.error('自动刷新筛选失败', e);
        } finally {
          setScreening(false);
        }
      }
    } catch (e) {
      console.error('自动刷新候选失败', e);
    }
  }, [fetchCandidates, loadDates]);

  useEffect(() => {
    loadDates();
    loadAutoTradeStatus();
    maybeAutoScreen();
    fetchConditions(); // 2026-09-06：拉取 buy_sort_by 条件用于候选股展示排序
  }, [loadDates, loadAutoTradeStatus, maybeAutoScreen, fetchConditions]);

  // 每 60s 兜底检查一次，覆盖「一直开着页面跨过 15:00」的场景
  useEffect(() => {
    const timer = window.setInterval(maybeAutoScreen, 60000);
    return () => window.clearInterval(timer);
  }, [maybeAutoScreen]);

  // 日期切换
  const handleDateChange = (e: SelectChangeEvent) => {
    const date = e.target.value;
    setSelectedDate(date);
    if (date === '') {
      fetchCandidates();
    } else {
      fetchCandidates(date);
    }
  };

  // 手动重新筛选（按最新条件）
  //  - 选了日期：按该日 daily_quotes 重跑，候选覆盖 candidates.date=selectedDate
  //  - 没选日期：按今天筛（受周末拦截）
  const handleRescreen = async () => {
    setScreening(true);
    try {
      const result = await evaluateConditions(selectedDate || undefined);
      const count = Array.isArray(result) ? result.length : (result as any)?.candidates?.length ?? 0;
      const scope = selectedDate ? `（${selectedDate} 历史）` : '（今日）';
      setSnackbar({ open: true, message: `筛选完成 ${scope}，共 ${count} 只候选股`, severity: 'success' });
      await loadDates();
      await fetchCandidates(selectedDate || undefined);
    } catch (e: any) {
      setSnackbar({ open: true, message: `筛选失败: ${e.message || e}`, severity: 'error' });
    } finally {
      setScreening(false);
    }
  };

  // 保存买入金额
  const handleSaveAmount = async () => {
    const amount = parseFloat(buyAmount);
    if (isNaN(amount) || amount < 1000) {
      setSnackbar({ open: true, message: '金额不能少于1000元', severity: 'error' });
      return;
    }
    setSavingAmount(true);
    try {
      await updateAutoTradeConfig({ buyAmountPerStock: amount });
      setSnackbar({ open: true, message: `买入金额已设为 ¥${amount.toLocaleString()}/只`, severity: 'success' });
      await loadAutoTradeStatus();
    } catch (e: any) {
      setSnackbar({ open: true, message: `保存失败: ${e.message}`, severity: 'error' });
    } finally {
      setSavingAmount(false);
    }
  };

  // 保存买入时间
  const handleSaveTime = async () => {
    const m = buyTime.match(/^(\d{2}):(\d{2})$/);
    if (!m) {
      setSnackbar({ open: true, message: '时间格式错误，请使用 HH:MM 格式', severity: 'error' });
      return;
    }
    const h = parseInt(m[1], 10);
    if (h < 9 || h > 14) {
      setSnackbar({ open: true, message: '买入时间需在 09:30 - 14:30 之间', severity: 'error' });
      return;
    }
    setSavingTime(true);
    try {
      await updateAutoTradeConfig({ buyTime });
      setSnackbar({ open: true, message: `买入时间已设为 ${buyTime}`, severity: 'success' });
      await loadAutoTradeStatus();
    } catch (e: any) {
      setSnackbar({ open: true, message: `保存失败: ${e.message}`, severity: 'error' });
    } finally {
      setSavingTime(false);
    }
  };

  // 买入时间选项（09:30 - 14:30，每30分钟）
  const buyTimeOptions: string[] = [];
  for (let h = 9; h <= 14; h++) {
    for (const m of [0, 30]) {
      if (h === 9 && m === 0) continue; // skip 09:00 (before market open)
      if (h === 14 && m === 30) continue; // skip 14:30 (too late)
      buyTimeOptions.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }

  // 为候选股补充实时行情（仅当前日期模式下，历史日必须用 DB 候选的 turnover/amp）
  // 2026-09-06：展示顺序与「撞顶时优先选」条件完全一致（worker autoTrade.ts 1-9 模式 byte-level 对齐）
  // 2026-09-06 修：selectedDate 非空时不覆盖实时行情（realtime 是今天的，DB 候选的才是那天的）
  useEffect(() => {
    if (candidates.length === 0) {
      setCandidatesWithQuotes([]);
      return;
    }

    const isHistorical = !!selectedDate;  // 历史日期模式：禁用实时数据覆盖

    const enriched = isHistorical
      ? candidates.map((c) => ({
          ...c,
          realtimePrice: c.price,           // 历史日：直接用 DB 候选价（那天的收盘价）
          changePct: 0,                     // 历史日：无涨跌幅概念（避免用今天的实时涨跌幅误导）
          turnoverRate: c.turnoverRate ?? 0, // 历史日：用 DB 候选的 turnoverRate（那天的）
        }))
      : (() => {
          const { quotes: allQuotes } = useMarketStore.getState();
          const quoteMap = new Map(allQuotes.map((q) => [q.code, q]));
          return candidates.map((c) => {
            const quote = quoteMap.get(c.code);
            return {
              ...c,
              realtimePrice: quote?.price ?? c.price,
              changePct: quote?.changePct ?? 0,
              turnoverRate: quote?.turnoverRate ?? c.turnoverRate ?? 0,
            };
          });
        })();

    // 读取 buy_sort_by 条件值
    const sortCond = conditions.find((c) => c.ruleType === 'buy_sort_by' && c.enabled);
    let sortBy = 3; // 默认综合分（与 autoTrade.resolveBuyExecutionParams fallback=3 对齐）
    if (sortCond) {
      const fromParams = parseInt(String(sortCond.params?.sortBy ?? ''), 10);
      const fromValue = parseInt(sortCond.value || '', 10);
      if (Number.isFinite(fromParams)) sortBy = fromParams;
      else if (Number.isFinite(fromValue)) sortBy = fromValue;
    }
    if (![1, 2, 3, 4, 5, 6, 7, 8, 9, 10].includes(sortBy)) sortBy = 3;

    const t = (c: Candidate & { turnoverRate: number; amplitude?: number }) => c.turnoverRate || 0;
    const a = (c: Candidate & { turnoverRate: number; amplitude?: number }) => c.amplitude || 0;
    const sorted = (() => {
      const arr = [...enriched] as Array<Candidate & { turnoverRate: number; amplitude?: number }>;
      if (sortBy === 1) return arr.sort((x, y) => t(y) - t(x));
      if (sortBy === 2) return arr.sort((x, y) => a(y) - a(x));
      if (sortBy === 3) return arr.sort((x, y) => (t(y) + a(y)) - (t(x) + a(x)));
      if (sortBy === 5) return arr.sort((x, y) => (t(y) * a(y)) - (t(x) * a(x)));
      if (sortBy === 6) return arr.sort((x, y) => (t(y) + 0.5 * a(y)) - (t(x) + 0.5 * a(x)));
      if (sortBy === 7) return arr.sort((x, y) => (0.5 * t(y) + a(y)) - (0.5 * t(x) + a(x)));
      if (sortBy === 8) return arr.sort((x, y) => (t(y) + 2 * a(y)) - (t(x) + 2 * a(x)));
      if (sortBy === 9) return arr.sort((x, y) => (2 * t(y) + a(y)) - (2 * t(x) + a(x)));
      if (sortBy === 10) return arr.sort((x, y) => a(x) - a(y)); // 振幅升序（小→大）
      // 4=字典序（默认），保留服务器返回顺序
      return arr.sort((x, y) => x.code.localeCompare(y.code));
    })();

    setCandidatesWithQuotes(sorted);
  }, [candidates, breadth?.snapshotTime, conditions, selectedDate]);

  const handleBuyClick = (candidate: any) => {
    const quote: RealtimeQuote = {
      code: candidate.code,
      name: candidate.name,
      price: candidate.realtimePrice,
      changePct: candidate.changePct,
      changeAmount: 0,
      open: 0,
      prevClose: 0,
      high: 0,
      low: 0,
      volume: 0,
      amount: 0,
      amplitude: 0,
      turnoverRate: candidate.turnoverRate,
      isSt: false,
      isLimitUp: false,
      isLimitDown: false,
    };
    setSelectedQuote(quote);
    setBuyDialogOpen(true);
  };

  const handleBuyConfirm = async (code: string, price: number, quantity: number) => {
    await buy(code, price, quantity);
    await fetchCandidates(selectedDate || undefined);
  };

  const isPauseBuy = breadth?.pauseBuy ?? false;
  const isPauseSell = breadth?.pauseSell ?? false;

  // ===== 次日待买入标注（2026-10-11 加）=====
  // 与后端 autoBuyAtOpen 口径严格一致：按当前排序（buy_sort_by）取前 maxStocks 只，
  // 于次日 buyTime 自动买入。历史日期模式、自动交易关闭、整体暂停时均不标注。
  const nextBuyMax = autoTradeStatus?.maxStocks ?? 9;
  const nextBuyBlockedReason = selectedDate
    ? '当前查看的是历史候选'
    : autoTradeStatus?.enabled === false ? '自动交易已关闭'
    : isPauseBuy ? '整体条件触发，禁止买卖'
    : '';
  // 与后端 autoBuyCandidates 同口径：取前 maxStocks 只后逐只判「整手」，
  // 价格过高不足 1 手的直接跳过且不补位（autoTrade.ts rawQty < 100 → continue）
  const { nextBuyList, nextBuySkippedCodes } = (() => {
    if (nextBuyBlockedReason) return { nextBuyList: [] as any[], nextBuySkippedCodes: new Set<string>() };
    const amount = Number(autoTradeStatus?.buyAmountPerStock || 0);
    const list: any[] = [];
    const skipped = new Set<string>();
    for (const c of candidatesWithQuotes.slice(0, nextBuyMax) as any[]) {
      const p = Number(c.realtimePrice || c.price || 0);
      if (amount > 0 && p > 0) {
        const floorQty = Math.floor(amount / p / 100) * 100;
        const ceilQty = floorQty + 100;
        const rawQty = Math.abs(ceilQty * p - amount) < Math.abs(floorQty * p - amount) ? ceilQty : floorQty;
        if (rawQty < 100) { skipped.add(c.code); continue; }
      }
      list.push(c);
    }
    return { nextBuyList: list, nextBuySkippedCodes: skipped };
  })();
  const nextBuyCodes = new Set<string>(nextBuyList.map((c: any) => c.code));

  // 2026-09-06：展示当前 buy_sort_by 排序模式（与回测 engine-core.js:67 1-9 完全一致）
  const sortCond = conditions.find((c) => c.ruleType === 'buy_sort_by' && c.enabled);
  let currentSortBy = 3;  // 默认综合分（与 autoTrade.resolveBuyExecutionParams fallback=3 对齐）
  if (sortCond) {
    const fromParams = parseInt(String(sortCond.params?.sortBy ?? ''), 10);
    const fromValue = parseInt(sortCond.value || '', 10);
    if (Number.isFinite(fromParams)) currentSortBy = fromParams;
    else if (Number.isFinite(fromValue)) currentSortBy = fromValue;
  }
  if (![1, 2, 3, 4, 5, 6, 7, 8, 9, 10].includes(currentSortBy)) currentSortBy = 3;
  const SORT_LABELS: Record<number, string> = {
    1: '换手率降序',
    2: '振幅降序',
    3: '综合分（换手+振幅）',
    4: '股票代码字典序',
    5: '综合分（换手×振幅）',
    6: '换手+0.5×振幅',
    7: '0.5×换手+振幅',
    8: '换手+2×振幅',
    9: '2×换手+振幅',
    10: '振幅升序（小→大）',
  };
  const sortLabel = SORT_LABELS[currentSortBy] || SORT_LABELS[3];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            买入候选看板
          </Typography>
          <Chip
            label={`排序：${currentSortBy}-${sortLabel}`}
            size="small"
            color="default"
            variant="outlined"
            title="与「撞顶时优先选」条件一致（条件管理页可改）"
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Chip
            label={`共 ${candidates.length} 只候选股`}
            color="primary"
            variant="outlined"
          />
          <Button
            variant="contained"
            size="small"
            startIcon={screening ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={handleRescreen}
            disabled={screening}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {screening ? '筛选中...' : '刷新筛选'}
          </Button>
        </Box>
      </Box>

      {/* 日期选择 + 买入金额配置 + 买入时间配置 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>选择日期</InputLabel>
            <Select
              value={selectedDate}
              label="选择日期"
              onChange={handleDateChange}
            >
              <MenuItem value="">
                <em>最新（自动）</em>
              </MenuItem>
              {availableDates.map((d) => (
                <MenuItem key={d.date} value={d.date}>
                  {d.date} ({d.count}只)
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small"
              label="自动买入金额/只"
              type="number"
              value={buyAmount}
              onChange={(e) => setBuyAmount(e.target.value)}
              sx={{ width: 180 }}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>¥</Typography>,
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveAmount}
              disabled={savingAmount}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {savingAmount ? <CircularProgress size={18} /> : '保存'}
            </Button>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>次日买入时间</InputLabel>
              <Select
                value={buyTime}
                label="次日买入时间"
                onChange={(e) => setBuyTime(e.target.value)}
              >
                {buyTimeOptions.map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              size="small"
              onClick={handleSaveTime}
              disabled={savingTime}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {savingTime ? <CircularProgress size={18} /> : '保存'}
            </Button>
          </Box>

          {autoTradeStatus && (
            <Chip
              label={autoTradeStatus.enabled ? '自动交易已开启' : '自动交易已关闭'}
              color={autoTradeStatus.enabled ? 'success' : 'default'}
              size="small"
            />
          )}
        </Stack>
      </Paper>

      {/* 系统自检状态卡片（每60秒自动自检，问题自动修复） */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <BugReportIcon fontSize="small" color={selfCheck?.status === 'error' ? 'error' : selfCheck?.status === 'fixed' ? 'warning' : 'success'} />
          <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            系统自检
          </Typography>
          {selfCheck ? (
            <>
              <Chip
                size="small"
                label={
                  selfCheck.status === 'ok' ? '正常'
                  : selfCheck.status === 'fixed' ? `已自动修复 ${selfCheck.issuesFixed} 项`
                  : `异常：${selfCheck.issuesFound - selfCheck.issuesFixed} 项待处理`
                }
                color={selfCheck.status === 'ok' ? 'success' : selfCheck.status === 'fixed' ? 'warning' : 'error'}
                variant="outlined"
              />
              <Typography variant="caption" color="text.secondary">
                上次自检：{new Date(selfCheck.checkTime).toLocaleTimeString('zh-CN')} · 耗时 {selfCheck.durationMs}ms
                {selfCheck.issuesFound > 0 && ` · 发现 ${selfCheck.issuesFound} 项`}
              </Typography>
            </>
          ) : (
            <Typography variant="caption" color="text.secondary">暂无自检记录</Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            size="small"
            startIcon={selfCheckRunning ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
            onClick={handleRunSelfCheck}
            disabled={selfCheckRunning}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {selfCheckRunning ? '自检中...' : '立即自检'}
          </Button>
          {selfCheck && selfCheck.issues.length > 0 && (
            <Button
              size="small"
              endIcon={<ExpandMoreIcon sx={{ transform: showSelfCheckDetail ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
              onClick={() => setShowSelfCheckDetail(!showSelfCheckDetail)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              详情
            </Button>
          )}
        </Box>
        {showSelfCheckDetail && selfCheck && selfCheck.issues.length > 0 && (
          <Box sx={{ mt: 1, borderTop: 1, borderColor: 'divider', pt: 1 }}>
            {selfCheck.issues.map((issue, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.25 }}>
                <Chip
                  size="small"
                  label={issue.fixed ? '已修复' : issue.severity === 'error' ? '待处理' : '告警'}
                  color={issue.fixed ? 'success' : issue.severity === 'error' ? 'error' : 'warning'}
                  sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
                />
                <Typography variant="caption" sx={{ lineHeight: 1.6 }}>
                  <strong>[{issue.check}]</strong> {issue.message}
                  {issue.fix && <span style={{ color: 'text.secondary' }}> — {issue.fix}</span>}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* 数据日期提示 */}
      {candidateDate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>筛选日期：{candidateDate}</strong>（{candidateSource}数据）
          {selectedDate && ' — 查看历史数据'}
        </Alert>
      )}

      {/* 次日买入计划（加粗标注的那一批） */}
      {candidates.length > 0 && (
        <Alert severity={nextBuyList.length > 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
          {nextBuyList.length > 0 ? (
            <>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                次日 {buyTime} 将自动买入 {nextBuyList.length} 只
                <Typography component="span" variant="caption" sx={{ fontWeight: 400, ml: 1, opacity: 0.75 }}>
                  （按「{currentSortBy}-{sortLabel}」取前 {nextBuyMax} 只，自动买入 ¥{Number(autoTradeStatus?.buyAmountPerStock || 0).toLocaleString()}/只）
                </Typography>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {nextBuyList.map((c: any, i: number) => (
                  <Chip
                    key={c.code}
                    size="small"
                    color="primary"
                    label={`${i + 1}. ${c.code} ${c.name}`}
                    sx={{ fontWeight: 700 }}
                  />
                ))}
              </Box>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.8 }}>
                下方表格中这些股票已加粗高亮显示
                {nextBuySkippedCodes.size > 0 && (
                  <> · 另有 {nextBuySkippedCodes.size} 只因股价过高（¥{Number(autoTradeStatus?.buyAmountPerStock || 0).toLocaleString()} 不足 1 手）将被跳过且不补位</>
                )}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              次日不会自动买入{nextBuyBlockedReason ? `：${nextBuyBlockedReason}` : ''}
            </Typography>
          )}
        </Alert>
      )}

      {/* 整体条件触发提示（双向：禁止买卖） */}
      {(isPauseBuy || isPauseSell) && !selectedDate && (
        <Alert severity="error" sx={{ mb: 2 }}>
          ⚠ 整体条件触发，今日禁止所有买卖
        </Alert>
      )}

      {candidates.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary">
              暂无候选股数据
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              候选股将在每日 15:00 收盘后更新，盘前可查看上一交易日数据
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ width: 100, fontWeight: 600, fontSize: 13, py: 1, px: 1.5, whiteSpace: 'nowrap' }}>代码</TableCell>
                <TableCell sx={{ width: 140, fontWeight: 600, fontSize: 13, py: 1, px: 1.5, whiteSpace: 'nowrap' }}>名称</TableCell>
                <TableCell sx={{ width: 80, fontWeight: 600, fontSize: 13, py: 1, px: 1.5, whiteSpace: 'nowrap' }} align="right">现价</TableCell>
                <TableCell sx={{ width: 80, fontWeight: 600, fontSize: 13, py: 1, px: 1.5, whiteSpace: 'nowrap' }} align="right">涨跌幅</TableCell>
                <TableCell sx={{ width: 60, fontWeight: 600, fontSize: 13, py: 1, px: 1.5, whiteSpace: 'nowrap' }} align="right">换手率</TableCell>
                <TableCell sx={{ width: 70, fontWeight: 600, fontSize: 13, py: 1, px: 1.5, whiteSpace: 'nowrap' }} align="right">振幅</TableCell>
                <TableCell sx={{ width: 110, fontWeight: 600, fontSize: 13, py: 1, px: 1.5, whiteSpace: 'nowrap' }} align="right">成交额</TableCell>
                <TableCell sx={{ width: 170, fontWeight: 600, fontSize: 13, py: 1, px: 1.5, whiteSpace: 'nowrap' }}>匹配条件</TableCell>
                {!selectedDate && <TableCell sx={{ width: 90, fontWeight: 600, fontSize: 13, py: 1, px: 1.5, whiteSpace: 'nowrap' }} align="center">操作</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {candidatesWithQuotes.map((c, idx) => {
                const isNextBuy = nextBuyCodes.has(c.code);
                const rank = idx + 1;
                return (
                <TableRow
                  key={c.code}
                  hover
                  className="stock-row"
                  sx={isNextBuy ? {
                    bgcolor: (t: any) => t.palette.mode === 'dark' ? 'rgba(144,202,249,0.14)' : 'rgba(25,118,210,0.09)',
                    '& .MuiTableCell-root': { fontWeight: 800 },
                  } : undefined}
                >
                  <TableCell sx={{
                    width: 100, py: 0.75, px: 1.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13,
                    ...(isNextBuy ? { borderLeft: '3px solid', borderLeftColor: 'primary.main', fontWeight: 800 } : {}),
                  }}>
                    <span style={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', fontWeight: isNextBuy ? 800 : 400 }}>
                      {isNextBuy ? `${rank}. ${c.code}` : c.code}
                    </span>
                  </TableCell>
                  <TableCell sx={{ width: 140, py: 0.75, px: 1.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13, fontWeight: isNextBuy ? 800 : 500 }}>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: '100%' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</span>
                      {isNextBuy && (
                        <Chip
                          label="次日买"
                          size="small"
                          color="primary"
                          sx={{ height: 18, '& .MuiChip-label': { px: 0.6, fontSize: 10, fontWeight: 700 } }}
                        />
                      )}
                      {!isNextBuy && nextBuySkippedCodes.has(c.code) && (
                        <Chip
                          label="不足1手"
                          size="small"
                          variant="outlined"
                          title={`股价 ¥${Number(c.realtimePrice || c.price || 0).toLocaleString()} 高于每笔预算可买的 1 手，次日将被跳过`}
                          sx={{ height: 18, '& .MuiChip-label': { px: 0.6, fontSize: 10 } }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 80, py: 0.75, px: 1.5, fontSize: 13 }} align="right">
                    <span style={{ color: getPriceColor(c.changePct), fontWeight: 600, fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
                      {formatNumber(c.realtimePrice)}
                    </span>
                  </TableCell>
                  <TableCell sx={{ width: 80, py: 0.75, px: 1.5, fontSize: 13 }} align="right">
                    <span style={{ color: getPriceColor(c.changePct), fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
                      {formatPct(c.changePct)}
                    </span>
                  </TableCell>
                  <TableCell sx={{ width: 60, py: 0.75, px: 1.5, fontSize: 13 }} align="right">
                    <span style={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
                      {formatNumber(c.turnoverRate)}%
                    </span>
                  </TableCell>
                  <TableCell sx={{ width: 70, py: 0.75, px: 1.5, fontSize: 13 }} align="right" title={`${formatNumber(c.amplitude)}%（20 日累计）`}>
                    <span style={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums', color: (c.amplitude ?? 0) >= 20 ? '#d32f2f' : 'inherit' }}>
                      {formatNumber(c.amplitude)}%
                    </span>
                  </TableCell>
                  <TableCell sx={{ width: 110, py: 0.75, px: 1.5, fontSize: 13 }} align="right">
                    <span style={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
                      {c.realtimePrice ? formatMoney(c.realtimePrice * 1000) : '--'}
                    </span>
                  </TableCell>
                  <TableCell sx={{ width: 170, py: 0.75, px: 1.5, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      {(() => {
                        try {
                          const rules = JSON.parse(c.matchedRules || '[]');
                          return Array.isArray(rules) ? rules.join(', ') : '';
                        } catch {
                          return '';
                        }
                      })()}
                    </Typography>
                  </TableCell>
                  {!selectedDate && (
                    <TableCell sx={{ width: 90, py: 0.75, px: 1.5 }} align="center">
                      <Box
                        component="button"
                        onClick={() => handleBuyClick(c)}
                        disabled={isPauseBuy || actionLoading}
                        sx={{
                          border: '1px solid',
                          borderColor: isPauseBuy ? 'grey.400' : 'primary.main',
                          color: isPauseBuy ? 'grey.500' : 'primary.main',
                          bgcolor: 'transparent',
                          borderRadius: 1,
                          px: 2,
                          py: 0.5,
                          cursor: isPauseBuy ? 'not-allowed' : 'pointer',
                          fontSize: 13,
                          fontWeight: 500,
                          '&:hover': isPauseBuy ? {} : { bgcolor: 'primary.main', color: '#fff' },
                        }}
                      >
                        买入
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <BuyConfirmDialog
        open={buyDialogOpen}
        quote={selectedQuote}
        onClose={() => setBuyDialogOpen(false)}
        onConfirm={handleBuyConfirm}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
